import { CSSProperties, ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * 종이 한 장. 화면에서는 A4 비율의 카드로, 인쇄할 때는 페이지 하나로 나간다.
 *
 * 색을 토큰(bg-card 등)으로 두면 다크 테마에서 검은 종이가 인쇄된다.
 * 여기만 리터럴 색을 쓴다 — 견적서는 어느 테마에서 보든 흰 종이다.
 */
export const SHEET_TEXT = "text-[#171717]";

interface SheetProps {
  children: ReactNode;
  className?: string;
}

// 앱 화면의 text-xs(12px)와 달리 종이는 인쇄 토큰(print-xs 0.7rem)을 쓴다 — 표 칸이 A4 폭에 맞춰져 있다.
export const SHEET_FONT_XS = "[--font-size-xs:var(--font-size-print-xs)]";

/** 인쇄 문서 한 장 — 화면에서는 A4 폭(max-w 210mm) 흰 카드, 인쇄 때는 한 페이지(`@page` A4 · 여백 14mm, `apps/web/src/app/globals.css`) · 색은 packages/ui/design/tokens.json 의 print-* 값만(print-ink #171717 글자 · print-line #d9d9d9 선 · print-shade-2 #f5f5f5 머리 칸 · print-paper 흰 종이) — 다크 테마에서도 같은 흰 종이 · 브랜드 네이비·화면 토큰(bg-card 등)은 종이에 쓰지 않는다 · 면허 경고 상자는 print-warn-line·print-warn-bg 에 글자는 print-ink · 페이지를 감싸는 `.print-root` 가 있으면 인쇄 때 앱 셸이 숨는다 · 안의 제목·칸은 같은 파일의 SheetTitle·SheetField·TH·TD 로. */
const PrintSheet = ({ children, className }: SheetProps) => {
  return (
    <section
      className={cn(
        "print-sheet mx-auto w-full max-w-[210mm] border border-[#d9d9d9] bg-[#ffffff] p-6 a4:p-8",
        SHEET_FONT_XS,
        SHEET_TEXT,
        className
      )}>
      {children}
    </section>
  );
};

export const SheetTitle = ({ children }: { children: ReactNode }) => (
  <h2 className="mb-4 text-center text-xl font-bold tracking-[0.3em]">
    {children}
  </h2>
);

/** 라벨·값 한 줄. 갑지의 표 대신 쓴다. */
export const SheetField = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div className="flex border-b border-[#e5e5e5]">
    <div className="w-24 shrink-0 bg-[#f5f5f5] px-2 py-1.5 text-xs font-semibold">
      {label}
    </div>
    <div className="min-w-0 flex-1 px-2 py-1.5 text-xs">{children}</div>
  </div>
);

export const TH = ({
  children,
  className,
}: {
  children?: ReactNode;
  className?: string;
}) => (
  <th
    className={cn(
      "border border-[#d9d9d9] bg-[#f5f5f5] px-1.5 py-1 text-xs font-semibold",
      className
    )}>
    {children}
  </th>
);

export const TD = ({
  children,
  className,
  colSpan,
  style,
}: {
  children?: ReactNode;
  className?: string;
  colSpan?: number;
  style?: CSSProperties;
}) => (
  <td
    className={cn("border border-[#d9d9d9] px-1.5 py-1 text-xs", className)}
    colSpan={colSpan}
    style={style}>
    {children}
  </td>
);

// 단위 칸(견적서 7% ≈ 36px, print-xs)은 한글 3자가 빠듯하다 — 한글·전각 2, 나머지 1 로 세어 6 부터 칸 안에서 접는다(넘치지 않으면 한 줄 그대로).
export const PRINT_UNIT_WRAP_AT = 6;

const WIDE_CHAR = /[\u1100-\u115f\u2e80-\ua4cf\uac00-\ud7a3\uf900-\ufaff\ufe30-\ufe4f\uff00-\uff60\uffe0-\uffe6]/;

// 인쇄 폭(반각 1·전각 2) — 글자 수로 재면 한글 4~7자가 칸을 넘친다.
export const printUnitWidth = (unit: string): number =>
  Array.from(unit).reduce((width, ch) => width + (WIDE_CHAR.test(ch) ? 2 : 1), 0);

export const PrintUnit = ({ unit }: { unit: string }) => {
  const long = printUnitWidth(unit) >= PRINT_UNIT_WRAP_AT;
  return (
    <span className={long ? "break-all" : "whitespace-nowrap"} data-print-unit={long ? "wrap" : undefined} title={long ? unit : undefined}>
      {unit}
    </span>
  );
};

export default PrintSheet;
